import { Injectable } from '@angular/core';
import { datadogRum, type Context } from '@datadog/browser-rum';

export interface DatadogRumConfig {
  applicationId: string;
  clientToken: string;
  site?: string;
  env: string;
  hostedEnvironment: string;
  service: string;
  version: string;
  sessionSampleRate?: number;
  sessionReplaySampleRate?: number;
  defaultPrivacyLevel?: string;
  beforeSend?: (event: unknown, context?: unknown) => boolean;
}

export interface DatadogRumUser {
  id: string;
  name?: string;
  email?: string;
  [key: string]: string | undefined;
}

export interface DatadogRumAccount {
  id: string;
  name?: string;
  [key: string]: string | undefined;
}

// RUM wrapper: init, setUser/setAccount, global context.
@Injectable()
export class DatadogRumService {
  private initialized = false;
  /** False until an account has been set — see clearAccount() for why this exists. */
  private accountIsSet = false;

  init(config: DatadogRumConfig): void {
    if (!config?.applicationId) return;
    try {
      datadogRum.init({
        applicationId: config.applicationId,
        clientToken: config.clientToken,
        site: config.site ?? 'datadoghq.com',
        env: config.env,
        version: config.version,
        service: config.service,
        sessionSampleRate: config.sessionSampleRate ?? 100,
        sessionReplaySampleRate: config.sessionReplaySampleRate ?? 0,
        defaultPrivacyLevel: (config.defaultPrivacyLevel as 'allow' | 'mask' | 'mask-user-input') ?? 'mask-user-input',
        beforeSend: config.beforeSend,
        telemetrySampleRate: 0,
      });
      this.initialized = true;
      this.setGlobalContextFromConfig(config);
    } catch {
      // no-op
    }
  }

  /** Call when RUM was already inited elsewhere (e.g. main.ts). Enables setUser/setAccount and sets global context. */
  connect(config: DatadogRumConfig): void {
    if (!config?.applicationId) return;
    this.initialized = true;
    this.setGlobalContextFromConfig(config);
  }

  private setGlobalContextFromConfig(config: DatadogRumConfig): void {
    if (!this.initialized) return;
    try {
      datadogRum.setGlobalContext({
        env: config.env,
        hostedEnvironment: config.hostedEnvironment,
        service: config.service,
        version: config.version,
      });
    } catch {
      // ignore
    }
  }

  setUser(user: DatadogRumUser | null): void {
    if (!this.initialized) return;
    if (!user) {
      this.clearUser();
      return;
    }
    try {
      datadogRum.setUser(user);
    } catch {
      // ignore
    }
  }

  setAccount(account: DatadogRumAccount | null): void {
    if (!this.initialized) return;
    if (!account?.id) {
      this.clearAccount();
      return;
    }
    try {
      datadogRum.setAccount(account);
      this.accountIsSet = true;
    } catch {
      // ignore
    }
  }

  clearUser(): void {
    if (!this.initialized) return;
    try {
      datadogRum.clearUser();
    } catch {
      // ignore
    }
  }

  /**
   * No-op unless an account was previously set. Calling the SDK's clearAccount() before it
   * finishes internal startup mutates the pre-start context, which bufferContextCalls later
   * replays as setContext({}) — tripping a spurious "property id of account is required"
   * console warning (v7). There is nothing to clear in that case anyway.
   */
  clearAccount(): void {
    if (!this.initialized || !this.accountIsSet) return;
    try {
      datadogRum.clearAccount();
      this.accountIsSet = false;
    } catch {
      // ignore
    }
  }

  /**
   * Reports a handled error to RUM with optional custom context (searchable as `@context.*`).
   * Use for failures the app swallows in its own error callbacks — e.g. a direct browser->S3
   * upload PUT that never reaches Angular's global ErrorHandler and so is otherwise invisible.
   */
  addError(error: unknown, context?: Record<string, unknown>): void {
    if (!this.initialized) return;
    try {
      datadogRum.addError(error, context);
    } catch {
      // ignore
    }
  }

  setGlobalContext(context: Record<string, unknown>): void {
    if (!this.initialized) return;
    try {
      datadogRum.setGlobalContext(context as Context);
    } catch {
      // ignore
    }
  }

  setGlobalContextProperty(key: string, value: unknown): void {
    if (!this.initialized) return;
    try {
      datadogRum.setGlobalContextProperty(key, value);
    } catch {
      // ignore
    }
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  getSessionId(): string | undefined {
    if (!this.initialized) return undefined;
    try {
      const ctx = datadogRum.getInternalContext?.();
      return (ctx as { session_id?: string } | undefined)?.session_id;
    } catch {
      return undefined;
    }
  }
}
